import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';

import { LanguagesService, minLessThanMaxValidator } from '@alpha-core/platform-ui';
import { FilterField } from '@alpha-core/platform-ui/interfaces/admin-filter.interface';
import { TranslateConfigPipe } from '@alpha-core/platform-ui/pipes/translate-config.pipe';
import { Subscription } from 'rxjs';
import { AlphaSharedModule } from '../../../shared/alpha-shared.module';
import { FilterDateRangeComponent } from '../../inbox/inbox-filters/filter-date-range/filter-date-range.component';
import { FilterEnumComponent } from '../../inbox/inbox-filters/filter-enum/filter-enum.component';
import { FilterNumberRangeComponent } from '../../inbox/inbox-filters/filter-number-range/filter-number-range.component';
import { FilterSearchRangeComponent } from '../../inbox/inbox-filters/filter-search-range/filter-search-range.component';

@Component({
    selector: 'app-edit-cutom-filter',
    standalone: true,
    imports: [
        InputTextModule,
        DropdownModule,
        ButtonModule,
        TableModule,
        AlphaSharedModule,
        FilterDateRangeComponent,
        FilterNumberRangeComponent,
        FilterSearchRangeComponent,
        FilterEnumComponent,
        TranslateConfigPipe,
    ],
    templateUrl: './edit-custom-filter.component.html',
    styles: [``],
})
export class EditCutomFilterComponent implements OnChanges, OnDestroy {
    private fb = inject(FormBuilder);

    dropdownIconClass = 'pi pi-chevron-down';
    languagesService = inject(LanguagesService);
    @Input() filterArray: any[] = [];
    @Input() selectedFilterType: 'filters' | 'additionalFilters' = 'filters';
    @Input() selectedFilters: Record<string, any> = {};
    @Input() selectedAdditionalFilters: Record<string, any> = {};
    @Output() onChangeFilter = new EventEmitter<Record<string, any>>();

    myForm!: FormGroup;
    selectedFilterArray: FilterField[] = [];
    filteredSelectedArray: FilterField[] = [];
    fields: FilterField[] = [];
    searchText: string = '';
    formValueSubscriber: Subscription | null = null;
    selectAll: boolean = false;
    additionalFiltersData: any[] = [];
    additionalFiltersSearchData: any[] = [];
    selectedAdditionalFiltersForm: Record<string, any> = {};

    ngOnChanges(changes: SimpleChanges) {
        if (changes['filterArray']) {
            this.fields = this.filterArray
                .flatMap((f) => f.fields || [])
                .map((f) => ({ ...f, langValue: this.languagesService.translateConfig(f?.label) }));
            this.refreshSelectedFilters();
        }
        if (changes['selectedFilters'] && changes['selectedFilters']?.firstChange) {
            this.refreshSelectedFilters();
        }
        this.makeAdditionalFilters();
    }

    private makeAdditionalFilters() {
        this.additionalFiltersData = this.fields.map((field) => {
            if (this.selectedAdditionalFilters[field.name]) {
                this.selectedAdditionalFiltersForm[field.name] = this.getFieldFormGroup(field).value;
            }
            return {
                ...field,
                checked: !!this.selectedFilters[field.name] || !!this.selectedAdditionalFilters[field.name],
                disabled: !!this.selectedFilters[field.name],
            };
        });
        this.additionalFiltersSearchData = this.additionalFiltersData;
    }

    private refreshSelectedFilters() {
        this.selectedFilterArray = this.filteredSelectedArray = [];
        this.setFilterForm();
    }

    getAvailableFields(currentIndex: number) {
        return this.fields.filter((field) => {
            const currentField = this.filteredSelectedArray[currentIndex];
            const curName = this.languagesService.translateConfig(currentField?.name);
            const fieldName = this.languagesService.translateConfig(field.name);
            if (currentField && curName === fieldName) return true;
            return !this.myForm.contains(fieldName);
        });
    }

    onDropdownOpen() {
        this.dropdownIconClass = 'pi pi-chevron-up';
    }

    onDropdownClose() {
        this.dropdownIconClass = 'pi pi-chevron-down';
    }

    onAddFilter() {
        this.selectedFilterArray.push({} as FilterField);
    }

    onRemoveFilter(index: number) {
        const field = this.selectedFilterArray[index];
        if (field?.name && this.myForm.contains(field.name)) {
            this.myForm.removeControl(field.name);
        }
        this.selectedFilterArray.splice(index, 1);
    }

    onFilterChange(event: DropdownChangeEvent, index: number) {
        const oldField = this.selectedFilterArray[index];
        const newField = event.value;

        if (oldField?.name && this.myForm.contains(oldField.name)) {
            this.myForm.removeControl(oldField.name);
        }

        this.selectedFilterArray[index] = newField;
        if (newField?.name) {
            this.myForm.addControl(newField.name, this.getFieldFormGroup(newField));
        }
    }

    setFilterForm() {
        const groupConfig: Record<string, FormGroup> = {};
        this.fields.forEach((field) => {
            if (this.selectedFilters[field?.name]) {
                this.selectedFilterArray.push(field);
                groupConfig[field.name] = this.getFieldFormGroup(field);
            }
        });
        this.myForm = this.fb.group(groupConfig);
        this.myForm.patchValue(this.selectedFilters || {});
        this.unSubscribeValueChanges();
        this.formValueSubscriber = this.myForm.valueChanges.subscribe((formValue) => {
            if (this.myForm.valid) {
                this.onChangeFilter.emit(formValue);
            }
        });
    }

    getFieldFormGroup(field: FilterField): FormGroup {
        if (!field.filterOptions) return new FormGroup({});

        switch (field.filterOptions.type) {
            case 'enum':
            case 'boolean':
                return this.buildEnumOrBooleanGroup(field);

            case 'number-range':
                return this.buildNumberRangeGroup(field);

            case 'date-range':
                return this.buildDateRangeGroup(field);

            default:
                return this.buildDefaultGroup(field);
        }
    }

    private buildEnumOrBooleanGroup(field: FilterField): FormGroup {
        const group: Record<string, any> = {
            type: new FormControl(field.filterOptions?.type),
            mapping: new FormControl(field.mapping),
            mappingType: new FormControl(field.mappingType),
            name: new FormControl(field.name),
            label: new FormControl(field.label),
        };

        if (field.filterOptions?.type === 'enum') {
            group['_exactMatch'] = new FormControl(field.filterOptions?._exactMatch || false);
        }

        (field.filterOptions?.options || []).forEach((opt: string) => {
            group[opt] = new FormControl(false);
        });

        return new FormGroup(group);
    }

    private buildNumberRangeGroup(field: FilterField): FormGroup {
        const group: Record<string, any> = {
            type: new FormControl('number-range'),
            mapping: new FormControl(field.mapping),
            mappingType: new FormControl(field.mappingType),
            name: new FormControl(field.name),
            minLabel: new FormControl('', [Validators.pattern(/^[1-9]\d*$/)]),
            maxLabel: new FormControl('', [Validators.pattern(/^[1-9]\d*$/)]),
            label: new FormControl(field.label),
        };

        return new FormGroup(group, { validators: minLessThanMaxValidator() });
    }

    private buildDateRangeGroup(field: FilterField): FormGroup {
        const group: Record<string, any> = {
            type: new FormControl(field.filterOptions?.type),
            mapping: new FormControl(field.mapping),
            mappingType: new FormControl(field.mappingType),
            name: new FormControl(field.name),
            value: new FormControl(null, this.customDateRangeValidator()),
            label: new FormControl(field.label),
        };

        return new FormGroup(group);
    }

    private buildDefaultGroup(field: FilterField): FormGroup {
        const group: Record<string, any> = {
            type: new FormControl(field.filterOptions?.type),
            mapping: new FormControl(field.mapping),
            mappingType: new FormControl(field.mappingType),
            name: new FormControl(field.name),
            value: new FormControl(null),
            label: new FormControl(field.label),
            _exactMatch: new FormControl(field.filterOptions?._exactMatch || false),
        };

        return new FormGroup(group);
    }

    customDateRangeValidator() {
        return (control: AbstractControl): ValidationErrors | null => {
            const value: any[] | null = control.value;
            if (Array.isArray(value) && (!value[0] || !value[1])) return { invalid: true };
            return null;
        };
    }

    onSearch() {
        const searchValue = this.searchText.toLowerCase().trim();
        const regex = new RegExp(searchValue, 'i');
        if (this.selectedFilterType === 'filters') {
            this.filteredSelectedArray = this.selectedFilterArray.filter((filter) =>
                regex.test(filter?.langValue?.toLowerCase()),
            );
        } else {
            this.additionalFiltersSearchData = this.additionalFiltersData.filter((filter) => {
                return regex.test(filter?.langValue?.toLowerCase());
            });
        }
    }

    onSelectAdditionalFilter(item: any) {
        if (item.disabled) return;
        item.checked = !item.checked;
        if (item.checked) {
            this.selectedAdditionalFiltersForm[item.name] = this.getFieldFormGroup(item).value;
        } else {
            delete this.selectedAdditionalFiltersForm[item.name];
        }
        this.onChangeFilter.emit(this.selectedAdditionalFiltersForm);
    }

    ngOnDestroy(): void {
        this.unSubscribeValueChanges();
    }

    unSubscribeValueChanges() {
        this.formValueSubscriber?.unsubscribe();
    }

    onSelectAll() {
        this.additionalFiltersData.forEach((filter) => {
            if (filter.disabled) return;
            filter.checked = this.selectAll;
            if (filter.checked) {
                this.selectedAdditionalFiltersForm[filter.name] = this.getFieldFormGroup(filter).value;
            } else {
                delete this.selectedAdditionalFiltersForm[filter.name];
            }
        });
        this.onChangeFilter.emit(this.selectedAdditionalFiltersForm);
    }
}
